import { type RouteRecordRaw, RouterView } from 'vue-router'
import { routeMiddleware, defaultLocale, supportedLocales, i18n } from '@/i18n'

const _customer = import.meta.env.VITE_CUSTOMER

const routeDefaultTranslation: Object = {
  de: {
    home: '',
    stopFinder: 'haltestellenauskunft',
    tripPlanner: 'verbindungsauskunft'
  },
  en: {
    home: '',
    stopFinder: 'stop-finder',
    tripPlanner: 'trip-planner'
  }
}

// Initialize with default translations
const routeTranslation = {};

// Function to load customer-specific route translations
async function loadCustomRouteTranslations() {
  try {
    // console.log(`Loading route translations for customer: ${_customer}`);

    // Dynamically import the customer-specific routes.json file
    const routesCustomModule = await import(`../_customer/${_customer}/routes.json`);
    const routesCustomTranslation = routesCustomModule.default;

    // console.log(`Loaded custom route translations:`, routesCustomTranslation);

    // Merge default translations with customer-specific translations
    supportedLocales.forEach((locale) => {
      routeTranslation[locale] = {
        ...routeDefaultTranslation[locale],
        ...routesCustomTranslation[locale]
      }
    });

    // console.log(`Final route translations after merging:`, routeTranslation);
  } catch (error) {
    console.error(`Failed to load custom route translations for customer: ${_customer}`, error);

    // If there's an error, use only the default translations
    supportedLocales.forEach((locale) => {
      routeTranslation[locale] = {
        ...routeDefaultTranslation[locale]
      }
    });

    console.log(`Using default route translations:`, routeTranslation);
  }
}

const getRoutePath = (routeName) => {
  const routePathIsOptional = routeTranslation[defaultLocale][routeName] === '' || routeTranslation[defaultLocale][routeName] === '/';

  const pathTranslationEntry = routePathIsOptional ? ':pathTranslation()?' : `:pathTranslation(${routeTranslation[defaultLocale][routeName]})`;

  return pathTranslationEntry;
}

const getRouteAliases = (routeName) => {
  const aliases = [];

  supportedLocales.forEach((locale) => {
    const currentRouteTranslation = routeTranslation[locale][routeName];
    const routePathIsOptional = currentRouteTranslation === '' || currentRouteTranslation === '/';

    if (locale !== defaultLocale) {
      let aliasEntry = `:pathTranslation(${routePathIsOptional ? routeDefaultTranslation[locale][routeName] : routeTranslation[locale][routeName]})`;

      if (routePathIsOptional) {
        aliasEntry += '?'
      }

      aliases.push(aliasEntry);
    }
  });

  return aliases;
}

export function i18nRoute(to: Object, locale: String) {
  locale = locale || defaultLocale;

  return {
    ...to,
    params: {
      ...to.params,
      locale: locale,
      pathTranslation: routeTranslation[locale][to.name],
    }
  }
}

// Function to create routes
function createRoutes(): Array<RouteRecordRaw> {
  return [
    {
      path: '/:locale?',
      beforeEnter: async (to, from, next) => {
        await routeMiddleware(to, from, next);

        const locale = to.params.locale || i18n.global.fallbackLocale;

        to.params.locale = locale;

        next();
      },
      beforeRouteUpdate: (to, from, next) => {
        const id = to.params.id
        this.AJAXRequest(id)
        next()
      },
      component: RouterView,
      children: [
        {
          name: 'home',
          path: getRoutePath('home'),
          alias: getRouteAliases('home'),
          component: () => import('../views/index.vue')
        },
        {
          name: 'stopFinder',
          path: getRoutePath('stopFinder'),
          alias: getRouteAliases('stopFinder'),
          component: () => import('../views/stop-finder.vue'),
          props: (route) => ({
            query: {
              location: route.query.location,
              isArrivalTime: route.query.isArrivalTime,
              date: route.query.date,
              time: route.query.time,
              login: route.query.login,
              userId: route.query.userId,
              password: route.query.password
            }
          }),
        },
        {
          name: 'tripPlanner',
          path: getRoutePath('tripPlanner'),
          alias: getRouteAliases('tripPlanner'),
          component: () => import('../views/trip-planner.vue'),
          props: (route) => ({
            query: {
              origin: route.query.origin,
              via: route.query.via,
              destination: route.query.destination,
              isArrivalTime: route.query.isArrivalTime,
              date: route.query.date,
              time: route.query.time,
              sharedTrip: route.query.sharedTrip,
              login: route.query.login,
              userId: route.query.userId,
              password: route.query.password
            }
          })
        },
        // {
        //   name: 'login',
        //   path: 'login',
        //   component: () => import('../components/login.vue'),
        //   props: (route) => ({
        //     query: route.query
        //   })
        // },
        {
          name: 'sandbox',
          path: 'sandbox',
          component: () => import('../views/sandbox-partials.vue')
        },
      ]
    },
    {
      path: '/api/playground',
      component: RouterView
    }
  ]
}

// Export a promise that resolves to the routes
export default (async () => {
  // Load customer-specific translations
  await loadCustomRouteTranslations();

  // Create and return the routes
  return createRoutes();
})();
